import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Form, Row } from 'antd';
import { FormProps, useForm } from 'antd/es/form/Form';
import { motion } from 'framer-motion';

// components
import ActionCreateHeaderComponent from 'modules/main/actions/createHeader';
import BackButton from 'components/backButton/backButton';
import PhoneInputComponent from 'components/phoneInput/phoneInput';
import FilePreview from 'components/filePreview/filePreview';
import SharesCount from 'components/sharesCount/sharesCount';
import PageTitle from 'components/title/title';
import DisabledElement from 'components/disabledElement/disabledElement';

// services
import NotificationService from 'core/services/notification';
import { onRequestShareholderInfo } from '../services/shareholderInfoServices';
import { isRTL } from 'core/services/language';
import { dateFormat } from 'utils/dateFormat';
import { formatFileSize } from 'utils/FileSize';

// models
import { ShareholderInfo } from '../models/shareholderInfo';
import useCooperative from 'core/hooks/useCooperative';

interface ShareholderInfoCreatePageProps {}

const ShareholderInfoCreatePage: FunctionComponent<ShareholderInfoCreatePageProps> = () => {
	const { t } = useTranslation();
	const { isCooperative } = useCooperative();

	const [checked, setChecked] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [shareholderInfo, setShareholderInfo] = useState<ShareholderInfo>();

	const checkNationalId = async (nationalId: string) => {
		try {
			setIsLoading(true);

			const shareholderInfoResponse = await onRequestShareholderInfo(nationalId);
			setShareholderInfo(shareholderInfoResponse);

			setChecked(true);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.SHAREHOLDER_NOT_FOUND'));
			setIsLoading(false);
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton styles={{ paddingTop: 16 }} />

			{!checked ? (
				<ActionCreateHeaderComponent
					title={t('ACTIONS.SHAREHOLDER_INFO')}
					subTitle={t('ACTIONS.FILL_TO_BUILD')}
					inputLabel={t('COMMON.NATIONAL_ID')}
					onSubmit={checkNationalId}
					isLoading={isLoading}
				/>
			) : (
				<motion.div
					initial={{ opacity: 0, transform: 'translateY(-10px)' }}
					animate={{ opacity: 1, transform: 'translateY(0)' }}
					transition={{ duration: 0.3 }}>
					<Card>
						<h5>{t('ACTIONS.SHAREHOLDER_INFO')}</h5>

						<Row gutter={[16, 16]}>
							<Col span={24} lg={12}>
								<DisabledElement
									fullWidth
									label={t('ACTIONS.SHAREHOLDER_FULL_NAME')}
									value={isRTL() ? shareholderInfo!.shareholder.name : (shareholderInfo!.shareholder.name_en ?? shareholderInfo!.shareholder.name)}
								/>
							</Col>
							<Col span={24} lg={12}>
								<DisabledElement fullWidth label={t('COMMON.NATIONAL_ID')} value={shareholderInfo!.shareholder.national_id} />
							</Col>

							<Col span={24} lg={12}>
								<DisabledElement fullWidth label={t('COMMON.DATE_OF_BIRTH')} value={dateFormat(shareholderInfo!.shareholder.date_of_birth, true)} />
							</Col>

							<Col span={24} lg={12}>
								<PhoneInputComponent
									disabled
									fullWidth
									userDialCode={shareholderInfo!.shareholder.phone_country_code}
									userPhoneNumber={shareholderInfo!.shareholder.phone_number}
								/>
							</Col>

							<Col span={24} lg={12}>
								<SharesCount
									fullWidth
									title={t('COMMON.COMMON_SHARES')}
									total={shareholderInfo!.common_shares_count}
									free={shareholderInfo!.free_common_shares_count}
									suspended={shareholderInfo!.suspended_common_shares_count}
									mortgaged={shareholderInfo!.mortgaged_common_shares_count}
								/>
							</Col>

							{!isCooperative && (
								<Col span={24} lg={12}>
									<SharesCount
										fullWidth
										title={t('COMMON.PREFERRED_SHARES')}
										total={shareholderInfo!.preferred_shares_count}
										free={shareholderInfo!.free_preferred_shares_count}
										suspended={shareholderInfo!.suspended_preferred_shares_count}
										mortgaged={shareholderInfo!.mortgaged_preferred_shares_count}
									/>
								</Col>
							)}

							<Col span={24} className="mt-3">
								<FilePreview
									name={t('ACTIONS.SHAREHOLDER_CERTIFICATE')}
									data={formatFileSize(shareholderInfo!.certificate_url.size_in_kilobyte)}
									downloadLink={shareholderInfo!.certificate_url.download_link}
								/>
							</Col>
						</Row>
					</Card>
				</motion.div>
			)}
		</>
	);
};

export default ShareholderInfoCreatePage;
